import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { fieldStyles } from './styles';

/**
 * Un campo con etiqueta que enseña su propio error.
 *
 * El hueco del error es lo que abarata cumplir la regla de `13 §6` —*«422
 * mapeado a los campos del formulario»*—: la pantalla vuelca `ApiError.fields`
 * sobre el formulario y cada mensaje aterriza bajo el campo que lo causó,
 * atado a él con `aria-describedby` para que un lector de pantalla también lo
 * anuncie.
 *
 * El error entra desplazándose un par de píxeles en lugar de aparecer: el
 * movimiento es lo que lleva la mirada de vuelta al campo que hay que corregir.
 */
export function Field({
  label,
  error,
  hint,
  className,
  ...input
}: { label: string; error?: string | undefined; hint?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label}
      </label>

      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : hint ? hintId : undefined}
        className={cn(fieldStyles, error ? 'border-danger focus:border-danger focus:shadow-danger/15' : 'border-line', className)}
        {...input}
      />

      {error ? (
        <p
          id={errorId}
          role="alert"
          className="animate-rise text-xs font-semibold text-danger"
        >
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-xs leading-relaxed text-ink-soft">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
